import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, X, ArrowRight, ShieldCheck, Truck, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const currency = (n) => `$${n.toFixed(2)}`;

export default function CartDrawer() {
  const { items, open, setOpen, remove, setQty, subtotal } = useCart();
  const [redirecting, setRedirecting] = useState(false);
  const [fulfillment, setFulfillment] = useState("ship"); // "ship" | "pickup"
  const [markets, setMarkets] = useState([]);
  const [marketId, setMarketId] = useState("");
  const [pickupDate, setPickupDate] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/markets`)
      .then((r) => {
        const list = r.data.markets || [];
        setMarkets(list);
        if (list.length && !marketId) setMarketId(list[0].id);
      })
      .catch(() => {});
  }, []);

  const close = () => setOpen(false);

  const goToStripe = async () => {
    if (items.length === 0) return;
    if (fulfillment === "pickup" && !marketId) {
      toast.error("Choose a market for pickup.");
      return;
    }
    // Group items by lookup_key (six-pack ids are dynamic per pack, so a cart
    // may have multiple six-pack line items — collapse them into one lookup_key
    // with summed quantity).
    const grouped = {};
    for (const it of items) {
      const key = it.lookup_key;
      if (!key) {
        toast.error(`${it.name} is missing a price key`);
        return;
      }
      grouped[key] = (grouped[key] || 0) + it.quantity;
    }
    const payload = {
      items: Object.entries(grouped).map(([lookup_key, quantity]) => ({
        lookup_key,
        quantity,
      })),
      origin_url: window.location.origin,
      fulfillment,
      market_id: fulfillment === "pickup" ? marketId : null,
      pickup_date: fulfillment === "pickup" ? pickupDate || null : null,
    };
    setRedirecting(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not start checkout. Try again."
      );
      setRedirecting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={close}
            className="fixed inset-0 z-50 bg-[color:var(--sr-primary-deep)]/45 backdrop-blur-md"
            data-testid="cart-backdrop"
          />
          <motion.aside
            key="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[440px] bg-[color:var(--sr-bg)] flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 md:px-8 h-20 border-b hairline-light">
              <p className="label-eyebrow text-[color:var(--sr-primary)]">
                Your bag
              </p>
              <button
                onClick={close}
                data-testid="close-cart-btn"
                className="p-3 -mr-3 min-h-[44px] min-w-[44px] flex items-center justify-center text-[color:var(--sr-text)]"
                aria-label="Close cart"
              >
                <X size={20} strokeWidth={1.25} />
              </button>
            </div>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center px-8 text-center gap-6">
                <p className="font-serif text-4xl text-[color:var(--sr-primary)] leading-tight">
                  A quiet, empty bag.
                </p>
                <p className="text-sm text-[color:var(--sr-muted)] max-w-xs">
                  Choose from five slow-crafted beverages, made in small
                  batches with intention.
                </p>
                <button
                  onClick={close}
                  data-testid="cart-empty-continue"
                  className="label-eyebrow underline underline-offset-8 decoration-[color:var(--sr-accent)] hover:text-[color:var(--sr-primary)]"
                >
                  Discover the collection
                </button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 md:px-8 py-6 divide-y divide-[color:var(--sr-border-light)]">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex gap-4 py-5 first:pt-0"
                      data-testid={`cart-item-${item.id}`}
                    >
                      <div className="w-20 h-24 bg-[color:var(--sr-secondary)] overflow-hidden shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 flex flex-col justify-between">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-serif text-xl leading-tight text-[color:var(--sr-primary)]">
                              {item.name}
                            </p>
                            {item.subtitle && (
                              <p className="text-[11px] text-[color:var(--sr-muted)] mt-1 leading-relaxed">
                                {item.subtitle}
                              </p>
                            )}
                            <p className="text-xs text-[color:var(--sr-muted)] mt-1">
                              {currency(item.price)}
                            </p>
                          </div>
                          <button
                            onClick={() => remove(item.id)}
                            data-testid={`remove-${item.id}`}
                            className="text-xs text-[color:var(--sr-muted)] hover:text-[color:var(--sr-primary)] label-eyebrow py-2 px-1 min-h-[44px] flex items-center"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() =>
                              setQty(item.id, Math.max(0, item.quantity - 1))
                            }
                            data-testid={`dec-${item.id}`}
                            className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                            aria-label="Decrease quantity"
                          >
                            <Minus size={12} strokeWidth={1.5} />
                          </button>
                          <span className="text-sm w-6 text-center tabular-nums">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => setQty(item.id, item.quantity + 1)}
                            data-testid={`inc-${item.id}`}
                            className="p-3 min-h-[44px] min-w-[44px] flex items-center justify-center border hairline hover:border-[color:var(--sr-primary)] transition-colors"
                            aria-label="Increase quantity"
                          >
                            <Plus size={12} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t hairline-light px-6 md:px-8 py-6 space-y-4">
                  {/* Fulfillment toggle */}
                  <div>
                    <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3">Fulfillment</p>
                    <div className="grid grid-cols-2 gap-2" data-testid="fulfillment-toggle">
                      <button
                        type="button"
                        onClick={() => setFulfillment("ship")}
                        data-testid="fulfillment-ship"
                        className={`flex items-center justify-center gap-2 px-3 py-3 border text-[11px] tracking-[0.24em] uppercase transition-colors ${
                          fulfillment === "ship"
                            ? "border-[color:var(--sr-primary)] bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)]"
                            : "hairline text-[color:var(--sr-primary)] hover:border-[color:var(--sr-primary)]"
                        }`}
                      >
                        <Truck size={14} strokeWidth={1.5} /> Ship
                      </button>
                      <button
                        type="button"
                        onClick={() => setFulfillment("pickup")}
                        data-testid="fulfillment-pickup"
                        className={`flex items-center justify-center gap-2 px-3 py-3 border text-[11px] tracking-[0.24em] uppercase transition-colors ${
                          fulfillment === "pickup"
                            ? "border-[color:var(--sr-primary)] bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)]"
                            : "hairline text-[color:var(--sr-primary)] hover:border-[color:var(--sr-primary)]"
                        }`}
                      >
                        <MapPin size={14} strokeWidth={1.5} /> Market pickup
                      </button>
                    </div>

                    {fulfillment === "pickup" && (
                      <motion.div
                        initial={{ opacity: 0, y: -6 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-4 space-y-3"
                        data-testid="pickup-fields"
                      >
                        <label className="block">
                          <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">Market</span>
                          <select
                            value={marketId}
                            onChange={(e) => setMarketId(e.target.value)}
                            data-testid="pickup-market-select"
                            className="w-full bg-transparent border-b hairline focus:border-[color:var(--sr-primary)] outline-none py-2 text-[color:var(--sr-primary)] font-serif text-lg"
                          >
                            {markets.map((m) => (
                              <option key={m.id} value={m.id}>{`${m.name} — ${m.day}`}</option>
                            ))}
                          </select>
                        </label>
                        <label className="block">
                          <span className="label-eyebrow text-[color:var(--sr-muted)] mb-2 block">Pickup date (optional)</span>
                          <input
                            type="date"
                            value={pickupDate}
                            onChange={(e) => setPickupDate(e.target.value)}
                            data-testid="pickup-date-input"
                            className="w-full bg-transparent border-b hairline focus:border-[color:var(--sr-primary)] outline-none py-2 text-[color:var(--sr-primary)] font-serif text-lg"
                          />
                        </label>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--sr-accent)]">
                          Free pickup — no shipping fee
                        </p>
                      </motion.div>
                    )}
                  </div>

                  <div className="border-t hairline-light pt-4">
                    <Row label="Subtotal" value={currency(subtotal)} strong />
                  <p className="text-[11px] text-[color:var(--sr-muted)] leading-relaxed">
                    Shipping, tax and any promo code are calculated at the
                    next step. Try <span className="text-[color:var(--sr-primary)]">SR10</span> for 10% off.
                  </p>
                  <button
                    onClick={goToStripe}
                    disabled={redirecting}
                    data-testid="checkout-btn"
                    className="w-full inline-flex items-center justify-between px-6 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
                  >
                    <span>{redirecting ? "Redirecting…" : "Continue to secure checkout"}</span>
                    <ArrowRight size={16} strokeWidth={1.25} />
                  </button>
                    <p className="flex items-center justify-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[color:var(--sr-muted)]">
                      <ShieldCheck size={12} strokeWidth={1.5} /> Payments by Stripe
                    </p>
                  </div>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

function Row({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between">
      <span
        className={`text-sm ${
          strong ? "text-[color:var(--sr-primary)]" : "text-[color:var(--sr-muted)]"
        }`}
      >
        {label}
      </span>
      <span
        className={`tabular-nums ${
          strong
            ? "font-serif text-2xl text-[color:var(--sr-primary)]"
            : "text-sm text-[color:var(--sr-text)]"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
